import React, { useEffect, useMemo, useState } from "react";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
import CollapsibleActionSection from "./CollapsibleActionSection";
import { loadJobsAtSameSite } from "../utils/siteJobs";
import { isRecurringJobRecord } from "../utils/recurringEditUtils";

const normalizeTechAssign = (value, keepBlank = false) => {
  if (Array.isArray(value)) {
    return keepBlank ? value : value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const statusOptions = ["Active", "Completed", "Invoice Sent", "On Hold"];

// ---- Same-site jobs (shown under Change Dates) ----------------------------
// Statuses that mean the job is done - not useful when lining up dates.
const CLOSED_STATUSES = ["completed", "invoice sent", "invoiced", "paid", "deleted", "cancelled", "canceled"];

const toDayKey = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const showDay = (k) => {
  if (!k) return "";
  const d = new Date(`${k}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "2-digit", day: "2-digit", year: "numeric" });
};
const daysBetween = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);
const addDays = (k, n) => {
  const d = new Date(`${k}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const techText = (j) => (Array.isArray(j.techAssign) ? j.techAssign.filter(Boolean).join(", ") : j.techAssign || "");
const recurrenceText = (j) => {
  const r = typeof j.recurrence === "object" ? j.recurrence?.type : j.recurrence || j.reccurance || j.recurring || j.repeat;
  return String(r || "").trim();
};

/**
 * Open jobs at the same site, split into: scheduled today or later (by date),
 * recurring (repeats - visit dates live on the calendar), and not scheduled yet.
 * Closed jobs, jobs that already ended, and this job itself are left out.
 */
export const groupSiteJobs = (siteJobs, currentJobId, today = todayKey()) => {
  const others = (siteJobs || []).filter((j) => j && j.id !== currentJobId);
  const isClosed = (j) => CLOSED_STATUSES.includes(String(j.status || "").toLowerCase().trim());
  const open = others.filter((j) => !isClosed(j));
  const recurring = open.filter((j) => isRecurringJobRecord(j));
  const oneOff = open.filter((j) => !isRecurringJobRecord(j));
  const scheduled = oneOff
    .map((j) => {
      const start = toDayKey(j.startDate);
      const end = toDayKey(j.endDate) || start;
      return { ...j, __start: start, __end: end < start ? start : end };
    })
    .filter((j) => j.__start && j.__end >= today)
    .sort((a, b) => (a.__start === b.__start ? String(a.wo).localeCompare(String(b.wo), undefined, { numeric: true }) : a.__start < b.__start ? -1 : 1));
  const unscheduled = oneOff.filter((j) => !toDayKey(j.startDate));
  // Counts so an empty list says WHY (all closed / all in the past).
  const closedCount = others.filter(isClosed).length;
  const pastCount = oneOff.length - scheduled.length - unscheduled.length;
  return { scheduled, recurring, unscheduled, total: others.length, closedCount, pastCount };
};

const siteRowStyle = (match) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 8,
  border: `1px solid ${match ? "#16a34a" : "#e2e8f0"}`,
  background: match ? "#f0fdf4" : "#f8fafc",
});
const smallBtn = {
  padding: "5px 9px",
  borderRadius: 6,
  border: "1px solid #1d4ed8",
  background: "#fff",
  color: "#1d4ed8",
  fontWeight: 700,
  fontSize: 12,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const buttonStyle = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #1d4ed8",
  background: "#dbeafe",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer",
};

const QuickJobFieldEditor = ({ job, usersList = [], onSave, disabled = false }) => {
  const [mode, setMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [siteJobs, setSiteJobs] = useState([]);
  const [siteJobsState, setSiteJobsState] = useState("idle"); // idle | loading | ready | error

  // Honors the Show In Dropdowns flag from the Staff Settings page and drops
  // deactivated staff. filterStaffForDropdowns already sorts by display name.
  const techOptions = useMemo(
    () =>
      filterStaffForDropdowns(usersList).map((user) => ({
        id: user.id,
        name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        role: user.role || "",
      })),
    [usersList]
  );

  useEffect(() => {
    setMode("");
    setDraft({});
  }, [job?.id, job?.occurrenceDate, job?.startDate]);

  // Change Dates: load the other jobs at this site so the same property can be
  // put on the same day. Read-only - nothing here changes the other jobs.
  useEffect(() => {
    if (mode !== "dates" || (!job?.siteName && !job?.propertyId)) {
      setSiteJobs([]);
      setSiteJobsState("idle");
      return undefined;
    }
    let live = true;
    setSiteJobsState("loading");
    loadJobsAtSameSite(job)
      .then((list) => {
        if (!live) return;
        setSiteJobs(list);
        setSiteJobsState("ready");
      })
      .catch((err) => {
        console.error("Same-site jobs lookup failed:", err);
        if (live) setSiteJobsState("error");
      });
    return () => {
      live = false;
    };
  }, [mode, job?.id, job?.siteName, job?.propertyId]);

  const siteGroups = useMemo(() => groupSiteJobs(siteJobs, job?.id), [siteJobs, job?.id]);

  // "Use this date": move the start to that day and keep this job's length.
  const applySiteDate = (dayKey) =>
    setDraft((prev) => {
      const start = prev.startDate ? toDayKey(prev.startDate) : "";
      const end = prev.endDate ? toDayKey(prev.endDate) : start;
      const length = start && end && end > start ? daysBetween(start, end) : 0;
      return { ...prev, startDate: dayKey, endDate: addDays(dayKey, length) };
    });

  const draftStart = toDayKey(draft.startDate);
  const draftEnd = toDayKey(draft.endDate) || draftStart;
  const overlapsDraft = (j) => draftStart && j.__start <= draftEnd && j.__end >= draftStart;

  const openEditor = (nextMode) => {
    if (!job) return;
    setMode(nextMode);
    setDraft({
      startDate: job.startDate || "",
      endDate: job.endDate || job.startDate || "",
      techAssign: normalizeTechAssign(job.techAssign),
      scope: job.scope || "",
      jobDescription: job.jobDescription || "",
      status: job.status || "Active",
    });
  };

  const save = async () => {
    if (!job || typeof onSave !== "function") return;

    const changes = {};
    if (mode === "dates") {
      if (!draft.startDate) {
        window.alert("Please select a start date.");
        return;
      }
      changes.startDate = draft.startDate;
      changes.endDate =
        !draft.endDate || draft.startDate > draft.endDate
          ? draft.startDate
          : draft.endDate;
    } else if (mode === "techs") {
      changes.techAssign = normalizeTechAssign(draft.techAssign);
    } else if (mode === "details") {
      changes.scope = draft.scope || "";
      changes.jobDescription = draft.jobDescription || "";
    } else if (mode === "status") {
      if (!draft.status) {
        window.alert("Please select a status.");
        return;
      }
      changes.status = draft.status;
    } else {
      return;
    }

    setSaving(true);
    try {
      const saved = await onSave(changes);
      if (saved === false) return;
      setMode("");
      setDraft({});
    } catch (error) {
      console.error("Quick job field update failed:", error);
      window.alert("Could not save this job change. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addTech = () =>
    setDraft((prev) => ({
      ...prev,
      techAssign: [...normalizeTechAssign(prev.techAssign, true), ""],
    }));

  const updateTech = (index, value) =>
    setDraft((prev) => {
      const next = [...normalizeTechAssign(prev.techAssign, true)];
      next[index] = value;
      return { ...prev, techAssign: next };
    });

  const removeTech = (index) =>
    setDraft((prev) => ({
      ...prev,
      techAssign: normalizeTechAssign(prev.techAssign, true).filter((_, i) => i !== index),
    }));

  return (
    <>
      <CollapsibleActionSection title="Edit Job" tone="edit" defaultOpen={false}>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("dates")}>
          Change Dates
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("techs")}>
          Add / Remove Techs
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("details")}>
          Edit Scope & Job Details
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("status")}>
          Change Status
        </button>
      </CollapsibleActionSection>

      {mode && (
        <>
          <div
            onClick={() => !saving && setMode("")}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 10020 }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(560px, 92vw)",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 24px 60px rgba(0,0,0,.3)",
              zIndex: 10021,
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {mode === "dates"
                ? "Change Job Dates"
                : mode === "techs"
                ? "Add / Remove Technicians"
                : mode === "status"
                ? "Change Job Status"
                : "Edit Scope & Job Details"}
            </h3>

            {mode === "dates" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Start Date
                  <input
                    type="date"
                    value={draft.startDate || ""}
                    onChange={(event) => {
                      const startDate = event.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        startDate,
                        endDate: !prev.endDate || startDate > prev.endDate ? startDate : prev.endDate,
                      }));
                    }}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <label style={{ fontWeight: 800 }}>
                  End Date
                  <input
                    type="date"
                    min={draft.startDate || undefined}
                    value={draft.endDate || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, endDate: event.target.value }))}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <div style={{ fontSize: 13, color: "#475569" }}>
                  When the new start date is after the current end date, the end date automatically changes to match the new start date.
                </div>

                {/* Other open jobs at this property - line them up on the same day. */}
                <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
                  <div style={{ fontWeight: 800, marginBottom: 2 }}>Other jobs at {job?.siteName || "this site"}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                    Open jobs scheduled today or later. Tap "Use this date" to put this job on the same day. Green = same day as the dates above.
                  </div>

                  {!job?.siteName && !job?.propertyId ? (
                    <div style={{ fontSize: 13, color: "#64748b" }}>This job has no site name, so there is nothing to compare.</div>
                  ) : siteJobsState === "loading" ? (
                    <div style={{ fontSize: 13, color: "#64748b" }}>Loading jobs at this site…</div>
                  ) : siteJobsState === "error" ? (
                    <div style={{ fontSize: 13, color: "#b91c1c" }}>Could not load the other jobs at this site. Check your connection and open Change Dates again.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {siteGroups.scheduled.length === 0 && (
                        <div style={{ fontSize: 13, color: "#64748b" }}>
                          {siteGroups.total === 0
                            ? "No other jobs found at this site."
                            : `No other open jobs scheduled from today on. Found ${siteGroups.total} other job${siteGroups.total === 1 ? "" : "s"} here: ${[
                                siteGroups.closedCount ? `${siteGroups.closedCount} completed / invoiced` : "",
                                siteGroups.pastCount ? `${siteGroups.pastCount} scheduled before today` : "",
                                siteGroups.recurring.length ? `${siteGroups.recurring.length} recurring` : "",
                                siteGroups.unscheduled.length ? `${siteGroups.unscheduled.length} not scheduled yet` : "",
                              ]
                                .filter(Boolean)
                                .join(", ")}.`}
                        </div>
                      )}
                      {siteGroups.scheduled.map((j) => {
                        const match = overlapsDraft(j);
                        return (
                          <div key={j.id} style={siteRowStyle(match)}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 14 }}>
                                {showDay(j.__start)}
                                {j.__end !== j.__start ? ` – ${showDay(j.__end)}` : ""}
                                {match ? <span style={{ color: "#15803d", marginLeft: 6 }}>✓ same day</span> : null}
                              </div>
                              <div style={{ fontSize: 12, color: "#475569" }}>
                                WO {j.wo || "—"}
                                {j.status ? ` · ${j.status}` : ""}
                                {techText(j) ? ` · ${techText(j)}` : ""}
                              </div>
                              {j.scope ? (
                                <div style={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {String(j.scope).slice(0, 100)}
                                </div>
                              ) : null}
                            </div>
                            <button type="button" style={smallBtn} disabled={saving} onClick={() => applySiteDate(j.__start)}>
                              Use this date
                            </button>
                          </div>
                        );
                      })}

                      {siteGroups.recurring.length > 0 && (
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
                          <b>Recurring at this site:</b>{" "}
                          {siteGroups.recurring
                            .map((j) => `WO ${j.wo || "—"} (${recurrenceText(j) || "repeats"}${j.scope ? ` · ${String(j.scope).slice(0, 40)}` : ""})`)
                            .join("; ")}
                          . Check the calendar for their visit dates.
                        </div>
                      )}

                      {siteGroups.unscheduled.length > 0 && (
                        <div style={{ fontSize: 12, color: "#475569" }}>
                          <b>Not scheduled yet:</b>{" "}
                          {siteGroups.unscheduled.map((j) => `WO ${j.wo || "—"}${j.scope ? ` (${String(j.scope).slice(0, 40)})` : ""}`).join("; ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {mode === "techs" && (
              <div>
                {normalizeTechAssign(draft.techAssign, true).map((tech, index) => (
                  <div key={index} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <select value={tech} onChange={(event) => updateTech(index, event.target.value)} style={{ flex: 1 }}>
                      <option value="">Select Technician</option>
                      {techOptions.map((option) => (
                        <option key={option.id} value={option.name}>
                          {option.name}{option.role ? ` (${option.role})` : ""}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removeTech(index)} style={{ ...buttonStyle, borderColor: "#b91c1c", background: "#fee2e2" }}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addTech} style={buttonStyle}>Add Technician</button>
              </div>
            )}

            {mode === "status" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Status
                  <select
                    value={draft.status || "Active"}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, status: event.target.value }))
                    }
                    style={{ width: "100%", marginTop: 6 }}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {mode === "details" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Scope
                  <input
                    type="text"
                    value={draft.scope || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, scope: event.target.value }))}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <label style={{ fontWeight: 800 }}>
                  Job Details / Description
                  <textarea
                    rows={8}
                    value={draft.jobDescription || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, jobDescription: event.target.value }))}
                    style={{ width: "100%", marginTop: 6, resize: "vertical" }}
                  />
                </label>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button type="button" disabled={saving} onClick={() => setMode("")} style={{ ...buttonStyle, borderColor: "#b91c1c", background: "#fee2e2" }}>
                Cancel
              </button>
              <button type="button" disabled={saving} onClick={save} style={{ ...buttonStyle, background: "#1d4ed8", color: "#fff" }}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default QuickJobFieldEditor;
